/**
 * Standings & tournament progression types (ADR-028 projections).
 * Derived from ManagedDraw.fixtures — not a separate aggregate.
 */

/**
 * @typedef {Object} StandingsScoringRules
 * @property {number} pointsForWin
 * @property {number} pointsForLoss
 * @property {number} [pointsForDraw]
 */

/**
 * @typedef {Object} StandingRow
 * @property {string} entryId
 * @property {string} label
 * @property {number} played
 * @property {number} won
 * @property {number} lost
 * @property {number} drawn
 * @property {number} matchesFor
 * @property {number} matchesAgainst
 * @property {number} gameDifference
 * @property {number} points
 * @property {number} rank
 * @property {boolean} [qualified]
 * @property {string} [tieBreakNote]
 */

/**
 * @typedef {Object} StandingsGroup
 * @property {string} groupId
 * @property {string} name
 * @property {string[]} entryIds
 * @property {StandingRow[]} rows
 */

/**
 * @typedef {Object} StandingsResult
 * @property {string} drawId
 * @property {string} format
 * @property {StandingsGroup[]} groups
 * @property {StandingsScoringRules} scoring
 */

/**
 * @typedef {Object} QualificationResult
 * @property {string} groupId
 * @property {number} topN
 * @property {string[]} qualifiedEntryIds
 * @property {StandingRow[]} qualifiedRows
 */

/**
 * @typedef {Object} TournamentProgress
 * @property {string} drawId
 * @property {string} format
 * @property {number | null} currentRoundIndex
 * @property {string | null} currentRoundName
 * @property {number} completedMatchCount
 * @property {number} remainingMatchCount
 * @property {number} totalPlayableMatchCount
 * @property {string | null} championEntryId
 * @property {string | null} championLabel
 * @property {boolean} isComplete
 * @property {number} percentComplete
 */

export const DEFAULT_STANDINGS_SCORING = Object.freeze({
  pointsForWin: 2,
  pointsForLoss: 0,
  pointsForDraw: 1,
});

export const FIXTURE_RESULT_STATUSES = Object.freeze({
  PENDING: "pending",
  BYE: "bye",
  COMPLETED: "completed",
  DONE: "done",
});

/**
 * @param {object | null | undefined} settings
 * @returns {StandingsScoringRules}
 */
export function resolveStandingsScoring(settings) {
  const custom = settings?.standings || settings?.standingsScoring || {};
  return {
    pointsForWin:
      custom.pointsForWin ?? custom.win ?? DEFAULT_STANDINGS_SCORING.pointsForWin,
    pointsForLoss:
      custom.pointsForLoss ?? custom.loss ?? DEFAULT_STANDINGS_SCORING.pointsForLoss,
    pointsForDraw:
      custom.pointsForDraw ?? custom.draw ?? DEFAULT_STANDINGS_SCORING.pointsForDraw,
  };
}

/**
 * @param {object | null | undefined} draw
 * @param {object | null | undefined} settings
 */
export function resolveQualifyTopN(draw, settings) {
  const fromDraw = draw?.qualificationTopN ?? draw?.qualifyTopN;
  if (fromDraw != null && Number.isFinite(Number(fromDraw))) {
    return Math.max(0, Math.floor(Number(fromDraw)));
  }
  const fromSettings =
    settings?.standings?.qualifyTopN ?? settings?.standings?.qualificationTopN;
  if (fromSettings != null && Number.isFinite(Number(fromSettings))) {
    return Math.max(0, Math.floor(Number(fromSettings)));
  }
  return 0;
}

/**
 * Fixture counts toward standings / progression completion.
 * @param {import('./drawEngineTypes').DrawFixture | object} fixture
 */
export function isFixtureComplete(fixture) {
  if (!fixture || fixture.isBye || fixture.status === FIXTURE_RESULT_STATUSES.BYE) {
    return true;
  }
  const st = fixture.status;
  if (st === FIXTURE_RESULT_STATUSES.COMPLETED || st === FIXTURE_RESULT_STATUSES.DONE) {
    return true;
  }
  return Boolean(fixture.winnerEntryId);
}

/**
 * Playable (non-bye) fixture awaiting a result.
 * @param {import('./drawEngineTypes').DrawFixture | object} fixture
 */
export function isFixturePendingPlay(fixture) {
  if (!fixture || fixture.isBye || fixture.status === FIXTURE_RESULT_STATUSES.BYE) {
    return false;
  }
  return !isFixtureComplete(fixture);
}
